import './index.scss';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, SnackBar } from '@umu/umu-design';
import { lang } from '@umu/ukit-lang';
import { INavigationPageAddProps, IOperateData, OPERATE_ACT_TYPE } from '../../interface/interface';
import { ModalFuncProps } from '@umu/design-base';
import {
    INavigationItemInfo,
    NavigationPageType,
    getNavigationListByPageType,
    setNavigationListByPageType,
} from '@umu/umu-api';
import { DragNavigationTable } from '../dragNavigationTable';
import { If } from 'common/src/components';
import cloneDeep from 'lodash/cloneDeep';
import { getAppNavigationColumns, getCoverData, getMergerList, onCheckEnableCount } from './dataFlow';
import { classSet } from '@umu/umu-dom';

const { confirm } = Modal;
/**
 * @desc 微页面配置信息弹窗页面
 */
const onSortNavListByEnabled = (list: INavigationItemInfo[]) => {
    return list.sort((a, b) => {
        return b.is_enabled - a.is_enabled;
    });
};
const AddNavigationPage = (props: INavigationPageAddProps) => {
    const {
        prefixCls = 'add-navigation-page-zone',
        className = '',
        defaultList = [],
        params = {},
        isHideFooter,
        cancelText,
        okText,
        isShowIconList = false,
        isShowColor = false,
        okProps = {},
        prefixElement = '',
        suffixElement = '',
        onSave,
        onClose,
        onSuccess,
        onUpdate,
        pageType = NavigationPageType.USE_NAVIGATION,
        isCloseInitLoading = false,
        onLoadNavigation,
        ...rest
    } = props;
    const configRef = useRef<Record<string, string>>({
        title_selected_color: '#222222',
    });
    const [isLoad, setIsLoad] = useState(true);
    const [list, setList] = useState([...defaultList]);
    const [isValid, setIsValid] = useState(false);
    const onNavListChange = (data: INavigationItemInfo[]) => {
        onUpdate && onUpdate(data);
        setList(data);
        isLoad && setIsLoad(true);
    };
    const initDataList = () => {
        const loadFunc = onLoadNavigation || getNavigationListByPageType;
        loadFunc(pageType, params).then((res) => {
            const { list = [], ...rest } = res || {};
            const newList = list.map((item, index) => {
                return {
                    ...item,
                    id: `${item.name}-${index}`,
                };
            });
            setList(onSortNavListByEnabled(newList));
            configRef.current = rest;
        });
    };

    useEffect(() => {
        if (!isCloseInitLoading) {
            initDataList();
        } else {
            setIsLoad(false);
        }
    }, []);

    const onUpdateListBySort = (startIndex: number, endIndex: number) => {
        if (endIndex >= list.length) {
            return false;
        }
        const result = cloneDeep(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        onNavListChange(onSortNavListByEnabled(result));
        return true;
    };
    const onSaveConfig = () => {
        const onSaveFunc = onSave || setNavigationListByPageType;
        onSaveFunc(
            list,
            {
                ...params,
                title_selected_color: '#222222',
            },
            pageType,
        ).then((res) => {
            if (onSuccess) {
                onSuccess({
                    isSuccess: res?.isSuccess,
                    params: params,
                    list,
                });
            } else {
                SnackBar.show({
                    message: res?.isSuccess ? lang('保存成功') : lang('保存失败'),
                });
            }
            res?.isSuccess && onClose?.();
        });
    };
    const onSubmit = () => {
        confirm({
            centered: true,
            title: lang('确认保存？'),
            content: lang('保存后，该功能导航设置将同步给平台内所有账户。'),
            okText: lang('保存'),
            cancelText: lang('取消'),
            okType: 'primary-blue',
            onOk: onSaveConfig,
        });
    };
    // 保存按钮文字信息
    const okTextName = useMemo(() => {
        return okText || lang('保存');
    }, [okText]);
    // 取消按钮文字
    const cancelTextName = useMemo(() => {
        return cancelText || lang('取消');
    }, [cancelText]);
    const onOperateCallback = (type: OPERATE_ACT_TYPE, data: IOperateData) => {
        const { index, step = 1 } = data;
        switch (type) {
            case OPERATE_ACT_TYPE.UP_MOVE:
                onUpdateListBySort(index, index - step);
                break;
            case OPERATE_ACT_TYPE.ICON_CHANGE:
                const newList = getCoverData(list, data);
                onNavListChange(newList);
                break;
            case OPERATE_ACT_TYPE.UPDATE_TITLE:
                if (!isValid) {
                    setIsValid(true);
                }
                onNavListChange(getMergerList(list, data));
                break;
            case OPERATE_ACT_TYPE.UPDATE_STATUS:
                const { isValid: checkIsValid, result } = onCheckEnableCount(list, data, list.length);
                onNavListChange(checkIsValid ? result : list);
                break;
            default:
                break;
        }
    };
    const { columns = [], showSecondLang } = getAppNavigationColumns({
        isValid,
        pageType,
        isShowIconList,
        isShowColor,
        onOperateCallback,
    });

    return (
        <div className={`${prefixCls} ${className}`} {...rest}>
            <If condition={!!prefixElement}>
                <Fragment>{prefixElement}</Fragment>
            </If>
            <div
                className={classSet({
                    [`${prefixCls}-content`]: true,
                    [`${prefixCls}-content-isValid`]: isValid,
                    [`${prefixCls}-content-isValid-has-second-col`]: showSecondLang,
                    [`${prefixCls}-content-isValid-no-has-second-col`]: !showSecondLang,
                })}
            >
                <DragNavigationTable
                    isLoading={isLoad}
                    className={`${prefixCls}-content-table`}
                    list={list}
                    columns={columns}
                    onUpdateListBySort={onUpdateListBySort}
                />
            </div>
            <If condition={!!suffixElement}>
                <Fragment>{suffixElement}</Fragment>
            </If>
            <If condition={!isHideFooter}>
                <div className={`${prefixCls}-footer`}>
                    <Button type="ghost-grey" className={`${prefixCls}-footer-col-split`} onClick={onClose}>
                        {cancelTextName?.toUpperCase()}
                    </Button>
                    <Button type="primary-blue" onClick={onSubmit} {...okProps}>
                        {okTextName?.toUpperCase()}
                    </Button>
                </div>
            </If>
        </div>
    );
};
/**
 * @desc 微页面配置信息弹窗页面 打开弹窗函数
 */
const onDialogAddNavigationPage = (data?: INavigationPageAddProps, dialogInfo?: ModalFuncProps) => {
    const { title = lang('功能导航设置'), ...rest } = dialogInfo || {};
    const dialog = confirm({
        title,
        centered: true,
        width: '8.4rem',
        footer: null,
        zIndex: 3000,
        className: 'add-navigation-pages-modal',
        ...rest,
        content: (
            <AddNavigationPage
                {...data}
                onClose={() => {
                    dialog.destroy();
                    data && data.onClose && data.onClose();
                }}
            />
        ),
    } as any);
    return dialog;
};
export { onDialogAddNavigationPage, AddNavigationPage };
